import { useEffect, useRef, useState } from 'react';
import type { NavEntry, NavKey } from './nav';
import { MoreIcon } from './icons';

/**
 * The phone top bar's overflow menu: the sections the tab bar has no room for
 * and no other phone surface carries. Settings was desktop-only, so on a 390px
 * phone a coach could not reach the scan list, the tape review or the client's
 * details at all.
 *
 * A plain disclosure rather than a dialog: it holds a few links, needs no
 * focus trap, and closes on a pick, Escape or a tap outside.
 */
export const PhoneMenu: React.FC<{
  entries: NavEntry[];
  active: NavKey;
  onSelect: (key: NavKey) => void;
}> = ({ entries, active, onSelect }) => {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  if (entries.length === 0) return null;

  return (
    <div ref={root} className="relative -mr-2">
      <button
        type="button"
        aria-label="More"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="phone-menu"
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-foreground-soft hover:bg-shell"
      >
        <MoreIcon size={20} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-[180px] rounded-lg border border-border bg-card p-1 shadow-[0_8px_24px_-8px_hsl(var(--foreground)/0.18)]"
        >
          {entries.map((e) => (
            <button
              key={e.key}
              type="button"
              role="menuitem"
              data-testid={`phone-menu-${e.key}`}
              aria-current={e.key === active ? 'page' : undefined}
              onClick={() => {
                setOpen(false);
                onSelect(e.key);
              }}
              className={`flex min-h-11 w-full items-center gap-2.5 rounded-md px-3 text-left text-[13px] font-medium ${
                e.key === active ? 'bg-accent text-accent-foreground' : 'text-foreground-soft hover:bg-shell'
              }`}
            >
              <e.Icon size={18} />
              {e.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
